/* import __COLOCATED_TEMPLATE__ from './product-filters.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ImportSource from 'embercom/models/import-service/import-source';

interface Signature {
  Args: { modal: any; importSource: ImportSource; onModalClose: () => void };
  Element: null;
}

export default class ShopifyProductFilters extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare notificationsService: any;

  @tracked vendor = '';
  @tracked productType = '';
  @tracked tag = '';
  @tracked isSaving = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    let rules = args.importSource?.prefilterRules;
    if (rules?.predicates) {
      for (let p of rules.predicates) {
        switch (p.descriptor_name) {
          case 'basic_vendor':
            this.vendor = p.value ?? '';
            break;
          case 'basic_product_type':
            this.productType = p.value ?? '';
            break;
          case 'tags_tag_names':
            this.tag = p.value ?? '';
            break;
        }
      }
    }
  }

  get hasFilters(): boolean {
    return !!(this.vendor || this.productType || this.tag);
  }

  get isSaveDisabled(): boolean {
    return this.isSaving;
  }

  private buildPredicates() {
    let predicates = [];
    if (this.vendor) {
      predicates.push({ attribute: 'basic_vendor', comparison: 'eq', value: this.vendor.trim() });
    }
    if (this.productType) {
      predicates.push({
        attribute: 'basic_product_type',
        comparison: 'eq',
        value: this.productType.trim(),
      });
    }
    if (this.tag) {
      predicates.push({ attribute: 'tags_tag_names', comparison: 'eq', value: this.tag.trim() });
    }
    return predicates;
  }

  @action updateVendor(e: InputEvent) {
    this.vendor = (e.target as HTMLInputElement).value;
  }

  @action updateProductType(e: InputEvent) {
    this.productType = (e.target as HTMLInputElement).value;
  }

  @action updateTag(e: InputEvent) {
    this.tag = (e.target as HTMLInputElement).value;
  }

  @action async saveFilters() {
    this.isSaving = true;
    try {
      await this.knowledgeHubService.updatePrefilterRules(
        this.args.importSource.id,
        this.buildPredicates(),
      );
      await this.args.importSource.reload();
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.save-success'),
      );
      this.args.onModalClose();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action async clearFilters() {
    this.isSaving = true;
    try {
      await this.knowledgeHubService.updatePrefilterRules(this.args.importSource.id, []);
      await this.args.importSource.reload();
      this.vendor = '';
      this.productType = '';
      this.tag = '';
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.clear-success'),
      );
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Shopify::ProductFilters': typeof ShopifyProductFilters;
    'knowledge-hub/setup-modal/sync-modal/shopify/product-filters': typeof ShopifyProductFilters;
  }
}
